/**
 * "Price valid through" — resolving it and formatting it.
 *
 * Two bugs lived here:
 *
 * 1. STALE DATE. `Proposal.expiresAt` is written only when the proposal is created
 *    and on revision finalize. Every display surface read `expiresAt`, so once a
 *    proposal existed, changing the date on the estimate never reached the
 *    proposal, the PDF, or the customer's view. The user's pick was saved
 *    correctly on the estimate all along — it just wasn't what got rendered.
 *
 *    The date to SHOW is the estimate's `priceValidThrough` read off the
 *    snapshot-aware estimate (`effectiveEstimate` here, `applyEstimateSnapshot`
 *    on the server): the LIVE value while the proposal is a draft, the FROZEN
 *    value once sent — so a sent proposal keeps the date the customer received.
 *
 * 2. TIMEZONE DRIFT. The value is a DateTime. The PDF renders server-side (Vercel
 *    runs UTC) while the web proposal renders in the viewer's browser, and both
 *    called `toLocaleDateString()` with no timeZone, so an instant near midnight
 *    UTC printed as two different calendar days on the two surfaces.
 *
 *    Formatting is pinned to UTC on both sides so they always agree. The date
 *    picker stores noon local time, which for US timezones lands mid-afternoon
 *    UTC on the same calendar day, so UTC formatting shows the day that was
 *    clicked.
 *
 * Keep in sync with paver-backend/src/utils/valid-through.ts.
 */

/** Pick the date to display: the estimate's value (snapshot-aware) wins. */
export function resolveValidThrough(
  estimate: { priceValidThrough?: Date | string | null } | null | undefined,
  proposal?: { expiresAt?: Date | string | null } | null,
): Date | null {
  const raw = estimate?.priceValidThrough ?? proposal?.expiresAt ?? null
  if (!raw) return null
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Format for display, pinned to UTC so the browser-rendered proposal and the
 * server-rendered PDF never disagree.
 */
export function formatValidThrough(value: Date | string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** The default when no date was ever picked: 30 days out. */
export function defaultValidThrough(now: Date = new Date()): string {
  return formatValidThrough(new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)) as string
}

/**
 * The string to print for a proposal — resolved, formatted, with the 30-day
 * fallback applied.
 */
export function validThroughLabel(
  estimate: { priceValidThrough?: Date | string | null } | null | undefined,
  proposal?: { expiresAt?: Date | string | null } | null,
  now: Date = new Date(),
): string {
  return formatValidThrough(resolveValidThrough(estimate, proposal)) ?? defaultValidThrough(now)
}
